<script lang="ts">
	import { DashDeviceWidget } from '@Shelly/shelly-design-system/index';
	import { status_configurator } from 'src/helpers/device_data_helpers';
	import { isCameraDevice, real_id } from 'src/helpers/interface_helpers';
	import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { devices } from 'src/stores/lists/devices';
	import { getDeviceState, defaultStateType } from 'src/stores/device-runtime/states';
	import { edit } from 'src/stores/ui/edit_lists';
	import { sidePanelStateCombined } from 'src/stores/ui/sidepanel-public';
	import { beforeUpdate, onDestroy } from 'svelte';
	import DashDeviceQuickActions from './components/DashDeviceQuickActions.svelte';
	import DashCameraSingleDeviceCard from './DashCameraSingleDeviceCard.svelte';
	import { getAdditionalText, getAvatar } from 'src/components/Dashboards/dash-helpers';
	import type { DashCameraWidgetConfig } from 'src/components/Dashboards/dash-types';
	import { getDeviceTimerState } from 'src/helpers/device_control_on_off';
	import type { DeviceTimerState } from 'src/helpers/device_control_on_off';
	import {
		getCriticalNotifications,
		getNotificationsForDevice,
	} from 'src/helpers/notifications/device_notifications_helper';

	export let deviceWidget;
	export let editMode = false;
	export let showFullscreen = false;
	export let cameraConfig: DashCameraWidgetConfig | undefined;

	let statuses;
	let notAuthenticated = false;
	let isLoading = false;
	let loadingRequest = false;

	let timerState: DeviceTimerState = { hasTimer: false, timer: null, timerStartedAt: undefined };
	let tickInterval: ReturnType<typeof setInterval> | null = null;
	let cachedTimerStartedAt: number | undefined;

	function startTimerTick() {
		stopTimerTick();
		tickInterval = setInterval(() => {
			if (!device || !status || !settings) return;

			const nextTimerState = getDeviceTimerState(device, status, settings);
			timerState = nextTimerState;

			if (!nextTimerState.hasTimer || !nextTimerState.timer?.time) stopTimerTick();
		}, 50);
	}

	function stopTimerTick() {
		if (tickInterval) {
			clearInterval(tickInterval);
			tickInterval = null;
		}
	}

	beforeUpdate(() => {
		if (device && Object.keys(device).length && Object.keys(deviceState).length) {
			statuses = status_configurator(status, device, settings);
			notAuthenticated = deviceState?.type === 9;
		}
	});

	onDestroy(() => stopTimerTick());

	$: deviceStateStore = getDeviceState(device?.id);
	$: deviceState = $deviceStateStore;
	$: device = $devices[deviceWidget?.id];
	$: statusStore = getDeviceStatus(device?.id);
	$: status = $statusStore;
	$: settingsStore = getDeviceSettings(device?.id);
	$: settings = $settingsStore;
	$: notificationsToShow = getNotificationsForDevice(device?.id);
	$: criticalNotifications = getCriticalNotifications(notificationsToShow);
	$: value = !device ? deviceWidget?.id : device?.name;
	$: avatar = getAvatar(device);
	$: isDefaultState = deviceState?.type == defaultStateType;
	$: additionalText = getAdditionalText(
		device,
		isUpdating,
		timerState?.hasTimer,
		timerState?.timer?.time,
		deviceState,
		criticalNotifications,
	);
	$: storeTimerState =
		device && status && settings
			? getDeviceTimerState(device, status, settings)
			: { hasTimer: false, timer: null, timerStartedAt: undefined };

	$: isLoading = deviceState?.isLoading;

	$: {
		if (storeTimerState.hasTimer && !loadingRequest && !isLoading && isDefaultState) {
			if (storeTimerState.timerStartedAt !== cachedTimerStartedAt) {
				cachedTimerStartedAt = storeTimerState.timerStartedAt;
				startTimerTick();
			} else if (!tickInterval) {
				startTimerTick();
			}
		} else {
			if (tickInterval) stopTimerTick();
			if (!loadingRequest && !isLoading && isDefaultState) {
				timerState = storeTimerState;
			} else {
				timerState = { hasTimer: false, timer: null, timerStartedAt: undefined };
				cachedTimerStartedAt = undefined;
			}
		}
	}

	$: isUpdating = deviceState?.type === 3;

	$: isOffline = !device || !status?.online;
	$: isCamera = isCameraDevice(device);
	$: hideSlotRight = !device || (device?.mainActionType === 'none' && isDefaultState) || $edit;
	$: isSelected = deviceWidget.id === $sidePanelStateCombined?.id;
</script>

{#if isCamera}
	<DashCameraSingleDeviceCard
		variant="button"
		{cameraConfig}
		{value}
		{additionalText}
		{editMode}
		showFullscreen={showFullscreen && !editMode}
		{device}
		{status}
		{settings}
		offline={isOffline}
		selected={isSelected}
		on:click
		on:arrow-click
		on:fullscreen
		on:handle-drag-start
		on:handle-drag-end
	/>
{:else}
	<DashDeviceWidget
		aria-label="Parameter widget card"
		isFocusable
		{avatar}
		{value}
		{additionalText}
		offline={isOffline}
		a11yProps={{
			indicatorOnAriaLabel: 'Label Indicator',
		}}
		{hideSlotRight}
		selected={isSelected}
		arrowRight
		on:click
		on:arrow-click
		on:handle-drag-start
		on:handle-drag-end
		{editMode}
	>
		<DashDeviceQuickActions
			slot="right"
			useV2Components={true}
			{device}
			{status}
			{settings}
			{statuses}
			{notAuthenticated}
		/>
	</DashDeviceWidget>
{/if}
